import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PaymentHandlers, PaymentMethodToken, PrimerClientError, ResumeToken, OnTokenizeSuccessHandler, OnResumeSuccessHandler, TokenizationHandlers } from '@primer-io/checkout-web';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import * as translator from '../../../utils/translator';
import { makePayment, submitPaymentDetails, handlePaymentResult, handlePaymentError, validateCustomerSession } from '../paymentService';
import { getPrimerRefusalMessage, savePrimerPendingPayment, clearPrimerPendingPayment, INSTRUMENT_TO_METHOD_TYPE, resolveVerificationType } from '../providers/primerHelpers';
import type { PaymentContext, PaymentCallbacks, PaymentResult, UserClickTracking } from '../paymentTypes';
import type { PaymentServiceOverrides } from './AdyenCheckoutForm';
import type { SubmitOutcome } from './checkoutFormProps';

type PrimerHandler = OnTokenizeSuccessHandler | OnResumeSuccessHandler;

type PrimerSdkHandlers = Required<Pick<TokenizationHandlers & PaymentHandlers,
  'onTokenizeShouldStart' | 'onTokenizeSuccess' | 'onResumeSuccess' | 'onResumeError' | 'onCheckoutFail'>>;

interface PrimerManualPaymentParams {
  paymentContext: PaymentContext;
  callbacks: PaymentCallbacks;
  tracking?: UserClickTracking;
  serviceOverrides?: PaymentServiceOverrides;
  // How an attempt ended, for a page that draws its own pay button; messages are shown by it.
  onSettled?: (outcome: SubmitOutcome, message?: string) => void;
  // The ID verification modal's submit when no payment is held: the form submits again.
  resubmit: () => void;
  onLoadError?: (error: unknown) => void;
}

/**
 * Universal Checkout's MANUAL payment flow, for repay links (the payment page uses primer-js, see
 * usePrimerJsCheckout): the token goes to make_payment, a required action continues with its client
 * token, the resume goes to payment_details, and an ID-verification method (PayPal on cannabis) is held
 * until the page's modal resolves. It also returns the SDK handlers the form binds (sdkHandlers). The
 * handlers read the latest props through refs (Primer binds them once per client token).
 */
export function usePrimerManualPayment({ paymentContext, callbacks, tracking, serviceOverrides, onSettled, resubmit, onLoadError }: PrimerManualPaymentParams) {
  const navigate = useNavigate();
  const latest = useRef({ paymentContext, callbacks, tracking, serviceOverrides, onSettled, resubmit, onLoadError, navigate });
  latest.current = { paymentContext, callbacks, tracking, serviceOverrides, onSettled, resubmit, onLoadError, navigate };
  const contextRef = useRef(paymentContext);
  contextRef.current = paymentContext;

  const paymentMethodTypeRef = useRef('');
  const primerPaymentIdRef = useRef('');
  // Set once the customer submitted: a checkout failure before that is a load failure.
  const hasTokenizedRef = useRef(false);
  // Tokenized payment held open while the customer completes ID verification
  const pendingVerificationRef = useRef<{ paymentMethodToken: PaymentMethodToken; handler: OnTokenizeSuccessHandler } | null>(null);

  const settle = useCallback((outcome: SubmitOutcome, message?: string) => latest.current.onSettled?.(outcome, message), []);

  const trackLoadError = useCallback((error: any) => {
    window.clientLogger.error('Primer checkout failed to load', error);
    MainTracker.track('custom', 'Primer Checkout Load Error', { code: error?.code, error: error?.message });
    latest.current.onLoadError?.(error);
  }, []);

  const settleResult = useCallback(async (result: PaymentResult, handler: PrimerHandler) => {
    const { paymentContext: context, callbacks: currentCallbacks, serviceOverrides: overrides } = latest.current;
    if (result.primerPaymentId) {
      primerPaymentIdRef.current = result.primerPaymentId;
    }

    // 3DS / redirect: the SDK runs the step and calls onResumeSuccess
    if (result.requiredAction?.clientToken) {
      savePrimerPendingPayment(context.hashID, primerPaymentIdRef.current);
      handler.continueWithNewClientToken(result.requiredAction.clientToken);
      return;
    }
    clearPrimerPendingPayment();

    const outcome = await handlePaymentResult({
      result,
      context,
      callbacks: currentCallbacks,
      navigate: (path: string) => latest.current.navigate(path),
      onSuccessOverride: overrides?.onSuccess,
    });

    if (outcome === 'success' || outcome === 'pending') {
      handler.handleSuccess();
      settle(outcome);
    } else if (outcome === 'express_unavailable' || outcome === 'express_prescription_unavailable') {
      // No payment was made - the page asks the customer to pick another delivery option first.
      // TODO(primer-spike): check how Universal Checkout renders a failure without a message.
      handler.handleFailure();
      settle('cancelled');
    } else {
      const message = getPrimerRefusalMessage(context.language, result.primerError);
      handler.handleFailure(message);
      settle('refused', message);
    }
  }, [settle]);

  const settleError = useCallback((error: any, handler: PrimerHandler) => {
    const { paymentContext: context, callbacks: currentCallbacks } = latest.current;
    clearPrimerPendingPayment();
    handlePaymentError({ error, context, callbacks: currentCallbacks });
    const message = getPrimerRefusalMessage(context.language, error?.response?.data?.primerError);
    handler.handleFailure(message);
    settle('error', message);
  }, [settle]);

  const callPaymentInServer = useCallback(async (paymentMethodToken: PaymentMethodToken, handler: OnTokenizeSuccessHandler, skipVerificationGate = false) => {
    const { paymentContext: context, callbacks: currentCallbacks, serviceOverrides: overrides, tracking: currentTracking } = latest.current;

    if (!overrides?.skipSessionValidation && !validateCustomerSession()) {
      handler.handleFailure();
      settle('cancelled');
      latest.current.navigate({ pathname: `/${context.country}/account/my-orders/${context.hashID}/details`, search: 'showRedirectModal=true' });
      return;
    }

    const paymentMethodType = paymentMethodTypeRef.current || INSTRUMENT_TO_METHOD_TYPE[paymentMethodToken.paymentInstrumentType] || paymentMethodToken.paymentInstrumentType;

    // ID verification gate (PayPal on cannabis orders): hold the token until the modal resolves
    const verificationType = resolveVerificationType(paymentMethodType, currentCallbacks.paymentTypesRequiringIdVerification);
    if (!skipVerificationGate && verificationType && !currentCallbacks.isIdVerified) {
      pendingVerificationRef.current = { paymentMethodToken, handler };
      currentCallbacks.onIdVerificationRequired?.(verificationType);
      return;
    }

    try {
      const result = await (overrides?.makePayment ?? makePayment)({
        gatewayState: { paymentMethod: { type: paymentMethodType } },
        context,
        provider: 'primer',
        primerParams: { paymentMethodToken: paymentMethodToken.token, paymentMethodType },
        tracking: currentTracking,
      });
      await settleResult(result, handler);
    } catch (error) {
      settleError(error, handler);
    }
  }, [settle, settleResult, settleError]);

  const callDetailsInServer = useCallback(async (resumeTokenData: ResumeToken, handler: OnResumeSuccessHandler) => {
    const { paymentContext: context, serviceOverrides: overrides } = latest.current;
    try {
      const result = await (overrides?.submitPaymentDetails ?? submitPaymentDetails)({
        provider: 'primer',
        hashID: context.hashID,
        primerPaymentId: resumeTokenData.paymentId || primerPaymentIdRef.current,
        resumeToken: resumeTokenData.resumeToken,
      });
      await settleResult(result, handler);
    } catch (error) {
      settleError(error, handler);
    }
  }, [settleResult, settleError]);

  // The page's ID verification modal submits through callbacks.onTriggerPaymentSubmit
  const triggerPaymentSubmit = useCallback(() => {
    const pending = pendingVerificationRef.current;
    pendingVerificationRef.current = null;
    if (pending) {
      // TODO(primer-spike): confirm a held token / client session survives a slow verification
      callPaymentInServer(pending.paymentMethodToken, pending.handler, true);
      return;
    }
    latest.current.resubmit();
  }, [callPaymentInServer]);

  useEffect(() => {
    if (callbacks.onTriggerPaymentSubmit !== undefined) {
      (callbacks as any).onTriggerPaymentSubmit = triggerPaymentSubmit;
    }
  }, [triggerPaymentSubmit, callbacks]);

  // Release the held payment when the modal closes without verification
  const prevCollapseKeyRef = useRef(callbacks.verificationCollapseKey ?? 0);
  useEffect(() => {
    const currentKey = callbacks.verificationCollapseKey ?? 0;
    if (currentKey > prevCollapseKeyRef.current) {
      prevCollapseKeyRef.current = currentKey;
      const pending = pendingVerificationRef.current;
      pendingVerificationRef.current = null;
      if (pending) {
        const message = translator.translate(latest.current.paymentContext.language, 'payment', 'primer/idVerificationRequired');
        pending.handler.handleFailure(message);
        settle('refused', message);
      }
    }
  }, [callbacks.verificationCollapseKey, settle]);

  const sdkHandlers = useMemo<PrimerSdkHandlers>(() => ({
    onTokenizeShouldStart: ({ paymentMethodType }) => {
      // The token itself doesn't carry the method type - remember it for make_payment
      if (paymentMethodType) paymentMethodTypeRef.current = paymentMethodType;
      hasTokenizedRef.current = true;
      return true;
    },
    onTokenizeSuccess: (paymentMethodToken, handler) => callPaymentInServer(paymentMethodToken, handler),
    onResumeSuccess: (resumeTokenData, handler) => { callDetailsInServer(resumeTokenData, handler); },
    onResumeError: (error: PrimerClientError) => {
      window.clientLogger.warn('Primer resume error', error);
      MainTracker.track('custom', 'Payment Post Failure', { Reason: `primer: ${error?.code || 'resume error'}`, "Payment Service Provider": contextRef.current.paymentServiceProvider });
      // 3DS / popup abandoned: back to the list, no message.
      settle('cancelled');
    },
    onCheckoutFail: (error: PrimerClientError) => {
      if (error?.code === 'PRIMER_TEARDOWN') return;
      if (!hasTokenizedRef.current) {
        trackLoadError(error);
        return;
      }
      window.clientLogger.warn('Primer checkout failed', error);
      settle('cancelled');
    },
  }), [callPaymentInServer, callDetailsInServer, trackLoadError, settle]);

  return { sdkHandlers, settle, trackLoadError, paymentMethodTypeRef, hasTokenizedRef, contextRef };
}
